import React, { useContext, useRef, useState } from 'react';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { enumToTitleCase } from '../../utils/TitleCase';
import { serviceActions } from '../../state/modules/service';
import { AlertType, IEntityMetaData, IMetaAction } from '../../graphql/schema/p4types';
import ModalContext from '../Context/ModalContext';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { cloneDeep } from '@apollo/client/utilities';

const PostSaveModals = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const [recentlyClickedClose, setRecentlyClickedClose] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const entityMetaData = cloneDeep(connectedForm?.values?.entityMetaData) as IEntityMetaData;
  if (
    connectedForm?.modal &&
    entityMetaData?.actions &&
    Array.isArray(entityMetaData.actions) &&
    entityMetaData.actions.length > 0
  ) {
    const configuredModals = connectedForm.modal!;

    //Scan metaData for postSave modal actions.
    let actions = entityMetaData.actions;

    //Filter out any actions that exist on this form or to whom the condition is not met.
    actions = actions.filter((action: IMetaAction) => {
      if (Object.keys(configuredModals).indexOf(action.actionType!) < 0) return false; //No matching modal on this form.
      const actionObject = configuredModals[action.actionType!]!;
      return !(actionObject.condition && !actionObject.condition(connectedForm));
    });

    const actionModals = actions
      .map((action: IMetaAction, index) => {
        const actionObject = configuredModals[action.actionType!]!;
        const { match, values } = connectedForm;
        const modalClose = (skip) => {
          if (recentlyClickedClose) return;
          setRecentlyClickedClose(true);
          setTimeout(() => {
            setRecentlyClickedClose(false);
          }, 2000);

          const spliceIndex = actions.findIndex((act) => act === action);

          //Remove action from metaData to remove the modal.
          if (spliceIndex >= 0) {
            actions.splice(index, 1);
          }
          entityMetaData.actions = actions;
          dispatch(connectedForm.change('entityMetaData', entityMetaData));

          if (skip) {
            const actionTitle = action?.actionType ? `(${enumToTitleCase(action.actionType)})` : '';

            dispatch(serviceActions.genericAlert(`Skipped`, `Document Print Skipped ${actionTitle}`, AlertType.Info));
          }

          //If no more actions to process, navigate back.
          //Passing values as a result since it 'should' be up to date as getting here means we're on a delayed submit response.
          if (actions.length === 0) connectedForm.afterSubmit!({ result: values });
        };

        let modalElement;
        if (actionObject.render) {
          modalElement = actionObject.render({
            modalOpen: true,
            metaAction: action,
            history,
            match,
            values,
            onClose: modalClose, //Would like to deprecate this - Use modalContext to close the modal.
            dispatch, //Would be good to deprecate this too since we can access in inner component with useDispatch().
          });
        } else {
          const ModalComponent = actionObject.component!;
          modalElement = <ModalComponent modalOpen={true} metaAction={action} />;
        }
        return {
          order: actionObject.order || 0,
          element: (
            <ConnectedFormContext.Provider value={{ ...connectedForm, disableModalClose: recentlyClickedClose }}>
              <ModalContext.Provider value={{ onClose: modalClose }}>{modalElement}</ModalContext.Provider>
            </ConnectedFormContext.Provider>
          ),
        };
      })
      .sort((a, b) => a.order - b.order);

    if (actionModals?.[0]?.element) {
      return actionModals[0].element;
    } else {
      console.warn(
        'An action was specified by the mutation, but all actions were filtered out by the UI, falling back to null to render.'
      );
    }
  }

  return null;
};

export default PostSaveModals;
